import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Kalenderen man huker av opptaksdagene i.
 *
 * ========  DAGENE ER IKKE EN PERIODE  ========
 *
 * Start og slutt rammer inn kalenderen, men en produksjon går ikke hver
 * dag mellom dem. Det er helger, det er opphold, og det er dager som
 * utgår. Derfor hukes hver dag av for seg — og det er avhukingen som
 * blir `shoot_day`, ikke intervallet.
 *
 * ========  NUMMERET FØLGER REKKEFØLGEN, IKKE DATOEN  ========
 *
 * Opptaksdag 1 er den første avhukede, 2 den neste. Det er slik bransjen
 * teller, og det er derfor `shoot_day.day` er tekst og ikke et tall som
 * regnes ut av datoen: «7B» er en gyldig dag, og en dag kan utgå uten at
 * nummereringen forskyves. Her settes de i rekkefølge; resten rettes i
 * Oppsett.
 */
export function Kalender({ fra, til, valgte, onEndre }: {
  readonly fra: string;
  readonly til: string;
  readonly valgte: ReadonlySet<string>;
  /** Hele settet, bygd av kalenderen. Ett valg, et dra, eller alle. */
  onEndre(neste: ReadonlySet<string>): void;
}): JSX.Element {
  const maaneder = useMemo(() => byggMaaneder(fra, til), [fra, til]);
  const alle = useMemo(
    () => maaneder.flatMap((m) => m.dager.map((d) => d.iso)), [maaneder]);

  /**
   * Draget som maler dager av eller paa.
   *
   * ========  TRYKK, HOLD OG DRA  ========
   *
   * Brukeren 03.11.2026: «Gjerne legg til at det gaar an aa trykke, holde
   * og dra, saa markerer man flere dager.» HOLDET er ikke pynt: kalenderen
   * staar i en flate man ruller, og et dra som malte med én gang ville
   * tatt rullingen fra en som bare skulle bla nedover. Et halvt sekund med
   * fingeren i ro sier at man mener det.
   *
   * MUSA SLIPPER HOLDET. Der finnes ingen rulling aa komme i veien for, og
   * en som drar med musa venter at det virker med det samme.
   *
   * MODUSEN SETTES AV DEN FOERSTE DAGEN: starter man paa en avhuket dag,
   * fjerner draget — ellers legger det til. Det er slik et malervalg
   * oppfoerer seg overalt ellers, og det gjoer angringen like lett som
   * valget.
   */
  const maling = useRef<{ paa: boolean; roert: boolean } | null>(null);
  const trykk = useRef<{ iso: string; x: number; y: number; timer: number } | null>(null);
  const [maler, setMaler] = useState(false);

  const sett = useCallback((iso: string, paa: boolean): void => {
    if (valgte.has(iso) === paa) return;
    const neste = new Set(valgte);
    if (paa) neste.add(iso); else neste.delete(iso);
    onEndre(neste);
  }, [valgte, onEndre]);
  /* Lest av lytterne, som ikke skal kobles opp paa nytt for hver tegning. */
  const settRef = useRef(sett);
  settRef.current = sett;

  const avbryt = useCallback((): void => {
    const t = trykk.current;
    if (t !== null) window.clearTimeout(t.timer);
    trykk.current = null;
  }, []);

  const start = useCallback((iso: string, naa: boolean): void => {
    maling.current = { paa: !naa, roert: false };
    setMaler(true);
    settRef.current(iso, !naa);
  }, []);

  /*
   * Fingeren foelges paa DOKUMENTET, ikke paa knappene.
   *
   * `pointerenter` fyrer ikke paa naboen under en beroering: den foerste
   * knappen fanger pekeren, og resten ser den aldri. `elementFromPoint` er
   * veien som virker for begge — og den samme flata bruker til
   * slettemodus i loggen.
   */
  useEffect(() => {
    if (!maler) return;
    const flytt = (e: PointerEvent): void => {
      const m = maling.current;
      if (m === null) return;
      m.roert = true;
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const dag = under?.closest<HTMLElement>("[data-dag]")?.dataset["dag"];
      if (dag !== undefined) settRef.current(dag, m.paa);
    };
    const slutt = (): void => { maling.current = null; setMaler(false); };
    document.addEventListener("pointermove", flytt);
    document.addEventListener("pointerup", slutt);
    document.addEventListener("pointercancel", slutt);
    return () => {
      document.removeEventListener("pointermove", flytt);
      document.removeEventListener("pointerup", slutt);
      document.removeEventListener("pointercancel", slutt);
    };
  }, [maler]);

  useEffect(() => avbryt, [avbryt]);

  if (maaneder.length === 0) {
    return (
      <p className="merknad">
        Velg start og slutt, så kommer kalenderen fram.
      </p>
    );
  }

  const altValgt = alle.length > 0 && alle.every((d) => valgte.has(d));

  /* Rekkefoelgen avgjoer nummeret, saa den maa vaere datoens og ikke
     avhukingens. Klikker man 5. mars etter 10. mars, er 5. fortsatt
     dagen foer. */
  const sortert = [...valgte].sort();

  return (
    <div className={`kal${maler ? " kal--maler" : ""}`}>
      {/*
        * ========  VELG ALLE  ========
        *
        * Brukeren 03.11.2026: «saa slipper jeg aa sitte og trykke paa alle
        * dagene om jeg skal velge mange.» En produksjon paa tolv uker er
        * aatti trykk, og de aller fleste av dem er ja.
        *
        * Veien tilbake er den samme knappen: er alt valgt, tar den alt
        * bort. Da finnes det ingen tilstand man kan male seg inn i uten
        * en vei ut.
        */}
      <div className="kal__topp">
        <span className="kal__teller">
          {valgte.size === 0
            ? "Ingen dager valgt"
            : valgte.size === 1 ? "1 opptaksdag" : `${valgte.size} opptaksdager`}
        </span>
        <button
          type="button"
          className="synk__knapp kal__alle"
          onClick={() => { onEndre(altValgt ? new Set() : new Set(alle)); }}
        >
          {altValgt ? "Fjern alle" : "Velg alle"}
        </button>
      </div>
      {maaneder.map((m) => (
        <div key={m.noekkel} className="kal__maaned">
          <span className="kal__navn">{m.navn}</span>
          <div className="kal__uker">
            {UKEDAGER.map((u) => (
              <span key={u} className="kal__ukedag" aria-hidden="true">{u}</span>
            ))}
            {m.tomme.map((n) => <span key={`tom${n}`} />)}
            {m.dager.map((d) => {
              const paa = valgte.has(d.iso);
              const nr = paa ? sortert.indexOf(d.iso) + 1 : null;
              return (
                <button
                  key={d.iso}
                  type="button"
                  data-dag={d.iso}
                  className={`kal__dag${paa ? " kal__dag--paa" : ""}`}
                  aria-pressed={paa}
                  aria-label={`${d.nr}. ${m.navn}${paa ? `, opptaksdag ${nr}` : ""}`}
                  onPointerDown={(e) => {
                    /* Musa maler med det samme; fingeren maa holde. Se
                       `maling` over for hvorfor. */
                    if (e.pointerType === "mouse") { start(d.iso, paa); return; }
                    avbryt();
                    const x = e.clientX;
                    const y = e.clientY;
                    trykk.current = {
                      iso: d.iso, x, y,
                      timer: window.setTimeout(() => {
                        trykk.current = null;
                        start(d.iso, paa);
                      }, HOLD_MS),
                    };
                  }}
                  onPointerMove={(e) => {
                    const t = trykk.current;
                    if (t === null) return;
                    /* Beveger fingeren seg foer holdet er inne, er det en
                       rulling — og den skal vinne. */
                    if (Math.abs(e.clientY - t.y) > 8 || Math.abs(e.clientX - t.x) > 8) {
                      avbryt();
                    }
                  }}
                  onPointerUp={() => {
                    const ventet = trykk.current !== null;
                    avbryt();
                    /* Kort trykk: ett valg. Var holdet inne, har draget
                       alt gjort jobben. */
                    if (ventet) sett(d.iso, !paa);
                  }}
                  onPointerCancel={avbryt}
                  /* Tastaturet: et «klikk» uten peker har `detail === 0`. */
                  onClick={(e) => { if (e.detail === 0) sett(d.iso, !paa); }}
                >
                  <span className="kal__tall">{d.nr}</span>
                  {nr === null ? null : <span className="kal__nr">{nr}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Hvor lenge fingeren maa staa i ro foer draget maler. Som i appen. */
const HOLD_MS = 450;

const UKEDAGER = ["m", "t", "o", "t", "f", "l", "s"] as const;
const MAANED = [
  "januar", "februar", "mars", "april", "mai", "juni",
  "juli", "august", "september", "oktober", "november", "desember",
] as const;

interface Dag { readonly iso: string; readonly nr: number }
interface Maaned {
  readonly noekkel: string;
  readonly navn: string;
  /** Tomme ruter foer den foerste, saa ukedagene stemmer. */
  readonly tomme: readonly number[];
  readonly dager: readonly Dag[];
}

function byggMaaneder(fra: string, til: string): readonly Maaned[] {
  if (fra === "" || til === "") return [];
  const start = new Date(`${fra}T00:00:00`);
  const slutt = new Date(`${til}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(slutt.getTime())) return [];
  if (start > slutt) return [];

  /* En produksjon kan gaa over et aar, men ikke over ti. Taket finnes for
     at en feiltastet dato ikke skal tegne tusen maaneder. */
  const ut: Maaned[] = [];
  const peker = new Date(start.getFullYear(), start.getMonth(), 1);
  while (peker <= slutt && ut.length < 36) {
    const aar = peker.getFullYear();
    const m = peker.getMonth();
    const iMaaneden = new Date(aar, m + 1, 0).getDate();
    const dager: Dag[] = [];
    for (let d = 1; d <= iMaaneden; d += 1) {
      const dato = new Date(aar, m, d);
      if (dato < start || dato > slutt) continue;
      dager.push({ iso: iso(dato), nr: d });
    }
    if (dager.length > 0) {
      /* Mandag foerst. `getDay()` gir soendag = 0, og en norsk kalender
         begynner ikke paa soendag. */
      const foerste = new Date(`${dager[0]?.iso ?? ""}T00:00:00`);
      const skift = (foerste.getDay() + 6) % 7;
      ut.push({
        noekkel: `${aar}-${m}`,
        navn: `${MAANED[m] ?? ""} ${aar}`,
        tomme: Array.from({ length: skift }, (_, i) => i),
        dager,
      });
    }
    peker.setMonth(peker.getMonth() + 1);
  }
  return ut;
}

/** Lokal dato som ISO. `toISOString()` gaar via UTC og bommer paa en dag. */
function iso(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const dag = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${dag}`;
}
